import { Screen } from 'components/screen';
import { BaseResponseFailure } from 'controllers/controller';
import { Errors } from 'errors';
import globalizationHelper from 'helpers/globalization.helper';
import createAccountScreenHTML = require('html-loader!html/create-account');
const ipify = require('ipify2');
import getQueryValue from 'helpers/get-query-value';
/**
 * Generates new account setup screens
 */
export class CreateAccountScreen extends Screen {
  public static readonly screenID: string = 'create-account';
  public static context;

  public async renderImpl(): Promise<void> {
    let urlParams;
    try {
      this.containerEl.innerHTML = createAccountScreenHTML;
      this.renderCountryOptions();
      this.renderStateOptions();
      this.renderMonthlySpendOptions();
      this.onCountryChange();
      urlParams = this.fetchUrlParams();
    } catch (e) {
      console.error(e);
    } finally {
      this.toggleFormElement('marin-loading-mask', false);
      // Define screen context if we want to store params for future so on browser refresh we can read it
      CreateAccountScreen.context = {};
    }

    if (urlParams?.email) {
      // Set the timeout for form elements to appear
      setTimeout(async () => {
        await this.submit();
      }, 0);
    }
  }

  /**
   * Fetch url params
   */
  public fetchUrlParams() {
    const companyName = getQueryValue('company_name') || '';
    const firstName = getQueryValue('first_name') || '';
    const lastName = getQueryValue('last_name') || '';
    const email = getQueryValue('email') || '';
    const country = getQueryValue('country') || DEFAULT_COUNTRY;
    const state = getQueryValue('state') || DEFAULT_STATE;
    const postalCode = getQueryValue('postal_code') || '12345';
    const monthlySpend = getQueryValue('monthly_spend') || SPEND_LIST[2].code;
    this.setFormValue({ companyName, firstName, lastName, email, state, postalCode, country, monthlySpend });
    return { companyName, firstName, lastName, email, state, postalCode, country, monthlySpend };
  }

  /**
   * Set form value
   */
  public setFormValue({ companyName, firstName, lastName, email, state, postalCode, country, monthlySpend }) {
    (document.getElementsByName('companyName')[0] as HTMLInputElement).value = companyName;
    (document.getElementsByName('firstName')[0] as HTMLInputElement).value = firstName;
    (document.getElementsByName('lastName')[0] as HTMLInputElement).value = lastName;
    (document.getElementById('loginID') as HTMLInputElement).value = email;

    if (state) {
      const stateList = globalizationHelper.getStatesOfUSA();
      const isValid = stateList.some((item) => item.code === state);
      if (isValid) {
        (document.getElementById('stateSelect') as HTMLInputElement).value = state;
      }
    }

    (document.getElementsByName('postalCode')[0] as HTMLInputElement).value = postalCode;

    if (country) {
      const countryList = globalizationHelper.getCountryList();
      const isValid = countryList.some((item) => item.code === country);
      if (isValid) {
        (document.getElementById('country') as HTMLInputElement).value = country;
      }
    }
    if (monthlySpend) {
      const isValid = SPEND_LIST.some((item) => item.code === monthlySpend);
      if (isValid) {
        (document.getElementById('monthlySpend') as HTMLInputElement).value = monthlySpend;
      }
    }
  }

  public renderCurrencyOptions(): void {
    let selectOptionList = (document.getElementById('currency') as any).options;
    const options = globalizationHelper.getSupportedCurrencies();
    options.forEach((option) => {
      let selected = false;
      // Make USD as selected option
      if (option === 'USD') {
        selected = true;
      }
      selectOptionList.add(new Option(option, option, selected, selected));
    });
  }

  public renderLocaleOptions(): void {
    let selectOptionList = (document.getElementById('locale') as any).options;
    const options = globalizationHelper.getSupportedLocales();
    options.forEach((option) => {
      let selected = false;
      // Make USD as selected option
      if (option === 'en_US') {
        selected = true;
      }
      selectOptionList.add(new Option(option, option, selected, selected));
    });
  }

  public renderCountryOptions(): void {
    let selectOptionList = (document.getElementById('country') as any).options;
    // Get all countries
    const countryList = globalizationHelper.getCountryList();
    countryList.forEach(({ name, code }) => {
      let selected = false;
      // Make US as default country
      if (code === 'US') {
        selected = true;
      }
      selectOptionList.add(new Option(name, code, selected, selected));
    });
  }

  public renderStateOptions(): void {
    let selectOptionList = (document.getElementById('stateSelect') as any).options;
    const stateList: any = globalizationHelper.getStatesOfUSA();
    stateList.forEach(({ name, code }) => {
      let selected = false;
      // Make CA as default state
      if (code === DEFAULT_STATE) {
        selected = true;
      }
      selectOptionList.add(new Option(name, code, selected, selected));
    });
  }

  public renderMonthlySpendOptions(): void {
    let selectOptionList = (document.getElementById('monthlySpend') as any).options;
    const optionList = SPEND_LIST;
    optionList.forEach(({ name, code }) => {
      let selected = false;

      if (code === SPEND_LIST[2].code) {
        selected = true;
      }
      selectOptionList.add(new Option(name, code, selected, selected));
    });
  }

  public onCountryChange(): void {
    this.onDropDownFormValueChange('country', (value) => {
      const stateDropdown = this.getFormDropdownInput('stateSelect');
      if (value === DEFAULT_COUNTRY) {
        stateDropdown.style.display = 'block';
      } else {
        stateDropdown.style.display = 'none';
      }
    });
  }

  public getStateValue(): string {
    const stateDropdown = this.getFormDropdownInput('stateSelect');
    let stateValue;
    if (stateDropdown.style.display === 'block') {
      stateValue = this.getSelectedDropDownValue('stateSelect');
    } else {
      stateValue = 'Alaska';
    }
    return stateValue;
  }
  /**
   * validate the email address to ensure gmail, hotmail and yahoo domains are not used.
   */
  public validateEmail(): void {
    const emailInput = document.getElementById('loginID') as HTMLInputElement | null;
    const domain: any = emailInput?.value.split('@')[1].split('.')[0];

    if (
      [
        'gmail',
        'googlemail',
        'hotmail',
        'yahoo',
        'outlook',
        'mail',
        'mailinator',
        'mailto',
        'ymail',
        'protonmail',
        'admin',
        'simphainvescorporation',
      ].includes(domain)
    ) {
      throw {
        errorMessage: Errors[Errors.INVALID_EMAIL],
      };
    }
  }

  public async submitImpl(): Promise<void> {
    try {
      this.toggleFormElement('marin-loading-mask', true);
      const companyName = this.getFormValue('companyName');
      const country = this.getSelectedDropDownValue('country');
      const firstName = this.getFormValue('firstName');
      const lastName = this.getFormValue('lastName');
      const state = this.getStateValue();
      const loginID = this.getFormValue('loginID');
      const email = loginID;
      const postalCode = this.getFormValue('postalCode');
      const monthlySpend = this.getSelectedDropDownValue('monthlySpend');
      let ssoAccount;
      const ip = await ipify.ipv64();
      this.validateEmail();
      await this.marinSSO.validateRequestCountry({ ip });
      try {
        // This will throw an error if login ID does not exist, catch that
        // if its a success means exist throw error
        ssoAccount = await this.marinSSO.getAccountIdP({ loginID });
      } catch (e) {
        console.error(e);
      } finally {
        if (ssoAccount?.success) {
          // If sso returns true that means account exists
          throw {
            errorMessage: Errors[Errors.CANNOT_CREATE_ACCOUNT_LOGIN_ID_EXISTS],
          };
        }
      }
      // Try catch to modify error object
      try {
        await this.marinSSO.createAccount({
          email,
          companyName,
          country,
          firstName,
          lastName,
          state,
          postalCode,
          monthlySpend,
        });
      } catch (err) {
        throw {
          errorMessage: err.message,
        };
      }

      await this.marinSSO.forgotPassword({
        loginID,
        newAccountSetup: 'true',
        siteID: this.marinSSO.siteID,
      });
    } catch (error) {
      // If error message exists
      throw error?.errorMessage
        ? error
        : {
            errorMessage: Errors[Errors.CANNOT_CREATE_CUSTOMER],
          };
    } finally {
      this.toggleFormElement('marin-loading-mask', false);
    }
  }

  protected getTitle(): string {
    return 'Sign Up';
  }

  protected shouldRenderCaptcha(): boolean {
    return false;
  }

  private validatePassword({ newPassword, confirmPassword }: { newPassword: string; confirmPassword: string }): void {
    if (newPassword !== confirmPassword) {
      const response: BaseResponseFailure = {
        success: false,
        errorMessage: Errors[Errors.PASSWORD_MISMATCH],
        errorDetails: undefined,
      };

      throw response;
    }
  }
}
const DEFAULT_STATE = 'CA';
const DEFAULT_COUNTRY = 'US';
const SPEND_LIST = [
  {
    code: 'USD_LESS_THAN_TEN_THOUSAND',
    name: 'Less than $10,000',
  },
  {
    code: 'USD_TEN_TO_FORTY_THOUSAND',
    name: '$10,000 - $40,000',
  },
  {
    code: 'USD_FORTY_TO_HUNDRED_THOUSAND',
    name: '$40,000 - $100,000',
  },
  {
    code: 'USD_HUNDRED_THOUSAND_TO_ONE_MILLION',
    name: '$100,000 - $1,000,000',
  },
  {
    code: 'USD_ONE_MILLION_AND_ABOVE',
    name: '$1,000,000 and above',
  },
];
